import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { fetchParkings } from '../store/parkingSlice';

const features = [
  { icon: '🗺️', title: 'Live Map View', desc: 'See all available parking spots in real-time on an interactive map of Chhatrapati Sambhaji Nagar.' },
  { icon: '⚡', title: 'Instant Booking', desc: 'Book your parking slot in seconds. No queues, no hassle — just tap and park.' },
  { icon: '💳', title: 'Secure Payments', desc: 'Pay safely via Razorpay with UPI, cards, or net banking. Get instant receipts.' },
  { icon: '📱', title: 'QR Entry', desc: 'Get a QR code for contactless entry. Show at the gate and drive right in.' },
  { icon: '⏱️', title: 'Extend Anytime', desc: 'Running late? Extend your parking time from the app without returning to your vehicle.' },
  { icon: '🔔', title: 'Smart Alerts', desc: 'Get notified before your time expires. Never get a fine for overstaying again.' },
];

const testimonials = [
  { name: 'Rahul Deshmukh', role: 'Daily Commuter', text: 'SmartPark saved me 20 minutes every day near Gulmandi. No more circling for spots!', rating: 5, avatar: 'R' },
  { name: 'Priya Kulkarni', role: 'IT Professional', text: 'The QR entry at IT Park is seamless. Booked from my desk, parked without stopping.', rating: 5, avatar: 'P' },
  { name: 'Amit Jadhav', role: 'Shop Owner', text: 'My customers love that they can pre-book parking near my store in Nirala Bazaar.', rating: 4, avatar: 'A' },
];

const stats = [
  { value: '10+', label: 'Parking Locations' },
  { value: '1,500+', label: 'Parking Slots' },
  { value: '5,000+', label: 'Happy Users' },
  { value: '₹20', label: 'Starting Price/hr' },
];

export default function HomePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { parkings, loading } = useSelector(s => s.parking);
  const [search, setSearch] = useState('');

  useEffect(() => {
    dispatch(fetchParkings({ limit: 6 }));
  }, [dispatch]);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/map?search=${search}`);
  };

  return (
    <div style={{ paddingTop: '64px' }}>
      {/* Hero Section */}
      <section style={{
        minHeight: '90vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'radial-gradient(ellipse at 50% 0%, rgba(99,102,241,0.15) 0%, transparent 70%)',
        padding: '60px 24px', textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        {/* Background grid */}
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.03,
          backgroundImage: 'linear-gradient(rgba(99,102,241,1) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,1) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
        }} />

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          style={{ maxWidth: '800px', position: 'relative' }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.3)',
              borderRadius: '100px', padding: '6px 16px', marginBottom: '24px',
              fontSize: '13px', color: '#a5b4fc',
            }}
          >
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', display: 'inline-block', animation: 'pulse 2s infinite' }} />
            Live parking available in Chhatrapati Sambhaji Nagar
          </motion.div>

          <h1 style={{
            fontSize: 'clamp(36px, 6vw, 72px)', fontWeight: '900', lineHeight: 1.1,
            marginBottom: '24px', letterSpacing: '-2px',
          }}>
            Find & Book Parking{' '}
            <span style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6, #ec4899)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              Instantly
            </span>
          </h1>

          <p style={{
            fontSize: '18px', color: '#94a3b8', marginBottom: '40px',
            lineHeight: 1.7, maxWidth: '600px', margin: '0 auto 40px',
          }}>
            The smartest way to park in Chhatrapati Sambhaji Nagar. Find available spots,
            book in seconds, and pay digitally — all from your phone.
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} style={{
            display: 'flex', gap: '12px', maxWidth: '560px', margin: '0 auto 40px',
            flexWrap: 'wrap', justifyContent: 'center',
          }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by location, landmark..."
              style={{
                flex: 1, minWidth: '280px', padding: '14px 20px',
                background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(99,102,241,0.3)',
                borderRadius: '12px', color: '#fff', fontSize: '15px', outline: 'none',
              }}
            />
            <button type="submit" style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: '#fff', border: 'none', borderRadius: '12px',
              padding: '14px 28px', fontSize: '15px', fontWeight: '700', cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}>
              🔍 Find Parking
            </button>
          </form>

          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/map" style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: '#fff', textDecoration: 'none', borderRadius: '12px',
              padding: '14px 32px', fontSize: '16px', fontWeight: '700',
            }}>
              🗺️ View Live Map
            </Link>
            <Link to="/register" style={{
              background: 'rgba(255,255,255,0.05)', color: '#e2e8f0',
              textDecoration: 'none', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)',
              padding: '14px 32px', fontSize: '16px', fontWeight: '600',
            }}>
              Get Started Free →
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Stats */}
      <section style={{ padding: '60px 24px', background: 'rgba(99,102,241,0.05)', borderTop: '1px solid rgba(99,102,241,0.1)', borderBottom: '1px solid rgba(99,102,241,0.1)' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '32px', textAlign: 'center' }}>
          {stats.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
              <div style={{ fontSize: '40px', fontWeight: '900', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{s.value}</div>
              <div style={{ color: '#94a3b8', fontSize: '14px', marginTop: '4px' }}>{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section style={{ padding: '80px 24px', maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '56px' }}>
          <h2 style={{ fontSize: '36px', fontWeight: '800', marginBottom: '16px' }}>
            Everything you need to <span style={{ color: '#6366f1' }}>park smarter</span>
          </h2>
          <p style={{ color: '#94a3b8', fontSize: '16px' }}>Built for the people of Chhatrapati Sambhaji Nagar</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
          {features.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              whileHover={{ y: -4 }}
              style={{
                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '16px', padding: '28px', cursor: 'default',
              }}
            >
              <div style={{ fontSize: '36px', marginBottom: '16px' }}>{f.icon}</div>
              <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '10px' }}>{f.title}</h3>
              <p style={{ color: '#94a3b8', fontSize: '14px', lineHeight: 1.6 }}>{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Available Parkings */}
      <section style={{ padding: '80px 24px', background: 'rgba(99,102,241,0.03)', borderTop: '1px solid rgba(99,102,241,0.1)' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h2 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '8px' }}>Popular Parking Spots</h2>
              <p style={{ color: '#94a3b8' }}>Top-rated locations across Chhatrapati Sambhaji Nagar</p>
            </div>
            <Link to="/map" style={{
              background: 'rgba(99,102,241,0.1)', color: '#6366f1',
              textDecoration: 'none', borderRadius: '10px', border: '1px solid rgba(99,102,241,0.3)',
              padding: '10px 20px', fontSize: '14px', fontWeight: '600',
            }}>
              View All →
            </Link>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>Loading parking spots...</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
              {parkings.slice(0, 6).map((p, i) => (
                <ParkingCard key={p._id || p.id || i} parking={p} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials */}
      <section style={{ padding: '80px 24px', maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h2 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '12px' }}>Loved by commuters</h2>
          <p style={{ color: '#94a3b8' }}>Real reviews from real users in CSN</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
          {testimonials.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              style={{
                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '16px', padding: '28px',
              }}
            >
              <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
                {[...Array(t.rating)].map((_, j) => <span key={j} style={{ color: '#f59e0b' }}>★</span>)}
              </div>
              <p style={{ color: '#cbd5e1', fontSize: '15px', lineHeight: 1.7, marginBottom: '20px' }}>"{t.text}"</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: '700', fontSize: '16px',
                }}>{t.avatar}</div>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '14px' }}>{t.name}</div>
                  <div style={{ color: '#64748b', fontSize: '12px' }}>{t.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{
        padding: '80px 24px', textAlign: 'center',
        background: 'linear-gradient(135deg, rgba(99,102,241,0.1), rgba(139,92,246,0.1))',
        borderTop: '1px solid rgba(99,102,241,0.2)',
      }}>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}>
          <h2 style={{ fontSize: '40px', fontWeight: '900', marginBottom: '16px' }}>
            Ready to park smarter?
          </h2>
          <p style={{ color: '#94a3b8', fontSize: '18px', marginBottom: '36px' }}>
            Join thousands of drivers saving time every day in Chhatrapati Sambhaji Nagar
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/register" style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: '#fff', textDecoration: 'none', borderRadius: '12px',
              padding: '16px 40px', fontSize: '17px', fontWeight: '700',
            }}>
              Start Parking Smarter 🚀
            </Link>
            <Link to="/map" style={{
              background: 'rgba(255,255,255,0.05)', color: '#e2e8f0',
              textDecoration: 'none', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)',
              padding: '16px 40px', fontSize: '17px', fontWeight: '600',
            }}>
              Explore Map
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer style={{
        padding: '40px 24px', textAlign: 'center',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#475569', fontSize: '14px',
      }}>
        <div style={{ marginBottom: '16px', fontWeight: '700', fontSize: '18px', color: '#fff' }}>
          🚗 Smart<span style={{ color: '#6366f1' }}>Park</span> CSN
        </div>
        <p>© 2026 SmartPark Chhatrapati Sambhaji Nagar. All rights reserved.</p>
        <p style={{ marginTop: '8px' }}>Making parking effortless in Maharashtra's heritage city.</p>
      </footer>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}

function ParkingCard({ parking }) {
  const navigate = useNavigate();
  const availPct = parking.totalSlots > 0 ? (parking.availableSlots / parking.totalSlots) * 100 : 0;
  const statusColor = availPct > 50 ? '#10b981' : availPct > 20 ? '#f59e0b' : '#ef4444';

  return (
    <motion.div
      whileHover={{ y: -4, boxShadow: '0 20px 40px rgba(99,102,241,0.15)' }}
      onClick={() => navigate(`/parking/${parking._id || parking.id}`)}
      style={{
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '16px', overflow: 'hidden', cursor: 'pointer', transition: 'all 0.3s',
      }}
    >
      <div style={{ position: 'relative', height: '160px', overflow: 'hidden' }}>
        <img
          src={parking.images?.[0] || 'https://images.unsplash.com/photo-1506521781263-d8422e82f27a?w=400'}
          alt={parking.name}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <div style={{
          position: 'absolute', top: '12px', right: '12px',
          background: 'rgba(0,0,0,0.7)', borderRadius: '8px', padding: '4px 10px',
          fontSize: '12px', fontWeight: '600', color: statusColor,
          border: `1px solid ${statusColor}40`,
        }}>
          {parking.availableSlots} slots free
        </div>
        {parking.is24Hours && (
          <div style={{
            position: 'absolute', top: '12px', left: '12px',
            background: 'rgba(99,102,241,0.9)', borderRadius: '6px', padding: '3px 8px',
            fontSize: '11px', fontWeight: '700', color: '#fff',
          }}>24/7</div>
        )}
      </div>
      <div style={{ padding: '16px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '6px' }}>{parking.name}</h3>
        <p style={{ color: '#64748b', fontSize: '12px', marginBottom: '12px' }}>📍 {parking.address}</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ fontSize: '20px', fontWeight: '800', color: '#6366f1' }}>₹{parking.pricePerHour}</span>
            <span style={{ color: '#64748b', fontSize: '12px' }}>/hr</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ color: '#f59e0b' }}>★</span>
            <span style={{ fontSize: '13px', fontWeight: '600' }}>{parking.rating?.toFixed(1) || '4.0'}</span>
            <span style={{ color: '#64748b', fontSize: '12px' }}>({parking.totalReviews || 0})</span>
          </div>
        </div>
        <div style={{ marginTop: '12px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {(parking.amenities || []).slice(0, 3).map((a, i) => (
            <span key={i} style={{
              background: 'rgba(99,102,241,0.1)', color: '#a5b4fc',
              borderRadius: '6px', padding: '2px 8px', fontSize: '11px',
            }}>{a}</span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}